import { ReactElement, useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Loader } from "@screencloud/screencloud-ui-components";
import { AuthMode, EditorStore, IdentityProvider } from "../../types";
import { RootState } from "../../reducers";
import { getEditorAuth, removeEditorAuth } from "../../actions";
import { ScreenCloudContext } from "../../ScreenCloudReactApp";

interface ThirdPartyAuthProps {
  authMode: AuthMode;
}

const openWindow = (url: string): void => {
  const width = 1024;
  const height = 768;
  // eslint-disable-next-line no-restricted-globals
  const top = screen.height / 2 - height / 2;
  // eslint-disable-next-line no-restricted-globals
  const left = screen.width / 2 - width / 2;
  window.open(
    url,
    "_blank",
    `width=${width},height=${height},top=${top},left=${left}`
  );
};

const slackUrl = (): string =>
  `${process.env.REACT_APP_SLACK_AUTH_URI}?scope=${
    process.env.REACT_APP_SLACK_SCOPES
  }&client_id=${process.env.REACT_APP_SLACK_CLIENT_ID}&redirect_uri=${
    process.env.REACT_APP_REDIRECT_URI
  }&state=${JSON.stringify({
    provider: IdentityProvider.Slack,
  })}`;

const teamsUrl = (): string =>
  `${process.env.REACT_APP_TEAMS_AUTH_URI}?client_id=${
    process.env.REACT_APP_TEAMS_CLIENT_ID
  }&response_type=code&state=${JSON.stringify({
    provider: IdentityProvider.Teams,
  })}&redirect_uri=${process.env.REACT_APP_REDIRECT_URI}&prompt=select_account`;

const ThirdPartyAuth = (
  props: ThirdPartyAuthProps
): ReactElement<ThirdPartyAuthProps> => {
  const dispatch = useDispatch();
  const editor: EditorStore = useSelector((state: RootState) => state.editor);
  const { logger } = useContext(ScreenCloudContext);

  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const { code, provider, closed } = event.data;
      if (event.data.source && event.data.source.includes("react")) {
        return;
      }
      if (closed) {
        return;
      }
      if (!code || !provider) {
        return;
      }

      const { channel } = editor;

      if (channel) {
        const { screenCloudOrgId, spaceId, arn } = channel;
        if (
          !(
            provider !== IdentityProvider.Slack &&
            provider !== IdentityProvider.Teams
          )
        ) {
          dispatch(
            getEditorAuth({
              provider,
              code,
              orgId: screenCloudOrgId,
              spaceId,
              arn,
              authMode: AuthMode.Private,
            })
          );
        }
      }
    };
    window.addEventListener("message", onMessage);

    return () => window.removeEventListener("message", onMessage);
  }, [dispatch, editor]);
  const { authMode } = props;

  if (editor.channel) {
    return !editor.channel.orgId ||
      editor.channel.orgId.length === 0 ||
      editor.channel.authMode !== authMode ? (
      <div className="row">
        <div className="label flex">
          {authMode === AuthMode.Slack ? "Slack" : "Microsoft"} Authentication
        </div>
        {editor.editorAuthenticating ? (
          <>
            <Loader
              size="medium"
              active
              inline="centered"
              className="auth-spinner"
            />
            <span className="auth-message">Logging you in...</span>
          </>
        ) : (
          <Button
            className="wide-button"
            primary
            onClick={() => {
              openWindow(authMode === AuthMode.Slack ? slackUrl() : teamsUrl());
              // setAuthMode(AuthMode.Private);
              //   logOut();
            }}
          >
            {`Authenticate ${
              authMode === AuthMode.Slack ? "Slack" : "Microsoft"
            } Account`}
          </Button>
        )}
      </div>
    ) : (
      <>
        <div className="row">
          <div className="label flex">
            {authMode === AuthMode.Slack ? "Slack" : "Microsoft"} Authentication
          </div>
          {editor.editorAuthenticating ? (
            <>
              <Loader
                size="medium"
                active
                inline="centered"
                className="auth-spinner"
              />
              <span className="auth-message">Logging you out...</span>
            </>
          ) : (
            <Button
              className="wide-button"
              primary
              onClick={() => {
                if (editor.channel) {
                  const { screenCloudOrgId, spaceId, channelId } =
                    editor.channel;
                  dispatch(
                    removeEditorAuth(
                      {
                        screenCloudOrgId,
                        spaceId,
                        channelId,
                        orgId: "",
                        orgName: "",
                      },
                      logger
                    )
                  );
                }

                // setAuthMode(AuthMode.Private);
                //   logOut();
              }}
            >
              Remove authentication
            </Button>
          )}
        </div>
      </>
    );
  }
  return <div />;
};

export default ThirdPartyAuth;
